import React, { ChangeEvent, useState } from 'react'
import DetailModalWrapper from '../DetailModalWrapper'
import s from './index.module.scss'
import { DeepPartial, FieldValues, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import Input from '../UI/Input'
import Button from '../UI/Button'
import { IRenderInputs } from '../../models/models'
import FileInput from '../UI/FileInput'
import Checkbox from '../UI/Checkbox'
import dateFormat from 'dateformat'
import Select from '../UI/Select'
import Loader from '../UI/Loader'
import CheckboxSwitcher from '../UI/CheckboxSwitcher'

interface IProps<T> {
	title: string
	inputs: IRenderInputs<T>[]
	schema: any
	initialData?: DeepPartial<T>
	isLoading?: boolean
	renderLoading?: boolean
	error?: string
	onSubmit: (data: T) => void
	closeHandler: () => void
}

const RenderInput = ({
	data,
	register,
	setValue,
	getValues,
	errors,
}: {
	data: IRenderInputs<any>
	register: any
	setValue: any
	getValues: any
	errors: any
}) => {
	const [checked, setChecked] = useState<boolean>(!!getValues(data.name))

	if (data.type === 'file') {
		return <FileInput name={data.name} label={data.placeholder} placeholder={data.placeholder} errors={errors} setValue={setValue} getValues={getValues} />
	}
	if (data.type === 'checkbox') {
		return (
			<div className={s.checkboxWrapper}>
				<Checkbox
					label={data.placeholder}
					name={data.name}
					checked={checked}
					onChange={(ev: ChangeEvent<HTMLInputElement>) => {
						setChecked(ev.target.checked)
						setValue(data.name as any, ev.target.checked as any)
					}}
				/>
			</div>
		)
	}
	if (data.type === 'switcher') {
		return (
			<div className={s.switcherWrapper}>
				<CheckboxSwitcher
					label={data.placeholder}
					name={data.name}
					checked={checked}
					onChange={(ev: ChangeEvent<HTMLInputElement>) => {
						setChecked(ev.target.checked)
						setValue(data.name as any, ev.target.checked as any)
					}}
				/>
			</div>
		)
	}
	if (data.type === 'select') {
		return <Select options={data.options || []} register={register} name={data.name} label={data.placeholder} placeholder={data.placeholder} errors={errors} />
	}
	return (
		<Input
			type={data.type === 'date-time' ? 'datetime-local' : data.type}
			register={register}
			name={data.name}
			label={data.placeholder}
			placeholder={data.placeholder}
			errors={errors}
			step={data?.step}
		/>
	)
}

function formatInitialData<T>(inputs: IRenderInputs<T>[], initialData?: DeepPartial<T>): T | undefined {
	if (!initialData) return undefined

	const dataEntries = Object.entries(initialData)

	return dataEntries.reduce((prev, [key, value]) => {
		const type = inputs.find((i) => i.name === key)?.type

		return {
			...prev,
			[key]: type === 'date' 
				? !!value ? dateFormat(value, 'yyyy-mm-dd') : ''
				: type === 'date-time' 
					? !!value ? dateFormat(value, 'yyyy-mm-dd HH:MM') : ''
					: value,
		}
	}, {}) as T
}

function CreateModal<T extends FieldValues>({ title, inputs, schema, initialData, isLoading, renderLoading, error, onSubmit, closeHandler }: IProps<T>) {
	const {
		register,
		handleSubmit,
		setValue,
		getValues,
		formState: { errors },
	} = useForm<T>({
		resolver: yupResolver(schema),
		values: formatInitialData(inputs, initialData),
	})

	const submitHandler = (data: T) => {
		onSubmit(data)
	}

	return (
		<DetailModalWrapper closeHandler={closeHandler}>
			<h3 className={s.title}>{title}</h3>
			{!!renderLoading ? (
				<Loader />
			) : (
				<form onSubmit={handleSubmit(submitHandler)}>
					<div className={s.formWrapper}>
						{inputs.map((i) => (
							<RenderInput key={i.name} data={i} errors={errors} getValues={getValues} register={register} setValue={setValue} />
						))}
					</div>

					{error && <p className={s.error}>{error}</p>}

					<Button variant={'filled'} type={'submit'} className={s.btn} disabled={isLoading}>
						{initialData ? 'Зберегти' : 'Додати'}
					</Button>
				</form>
			)}
		</DetailModalWrapper>
	)
}

export default CreateModal
